import { useGpuPlacement } from "../useGpuPlacement";
import { measureDiscover, type DiscoverMeasurementView } from "./discoverMeasurement";
import { plural, t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { qaVisible } from "../qa/qaClient";
import { appStartup, recordStartup } from "@/performance/startupTiming";
import { useDiscoveryView } from "./useDiscoveryView";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Play, Plus, RotateCcw, X, Library, ArrowDown, ArrowUpRight, CircleCheck, EyeOff, CircleHelp, Search, AppWindow, Flag } from "lucide-react";
const ReportCapsuleModal = lazy(() => import("@/components/runs/ReportCapsuleModal"));
import { useDiscoveryPanels } from "@/components/shell/DiscoveryPanels";
import { isDiscoveryLibraryRailEnabled } from "@/config/featureFlags";
// The frame host (session console, terminal and pixel transports) loads when
// an app opens, not with the Discover grid.
const RetainedAppFrames = lazy(() => import("@/components/RetainedAppFrames"));
import DeferredView from "@/components/DeferredView";
import GlobalFooter from "@/components/GlobalFooter";
import type { RunFeedItem } from "@/components/runs/feedItem";
import { useSearch } from "@/components/search/SearchProvider";
import { AppIcon } from "../AppIcon";
import { addDiscoverReadyApp, discoverClient, type DiscoverApp } from "../discoverClient";
import InstanceLaunch from "../InstanceLaunch";
import {
  adoptMyAppsUser,
  refreshMyApps,
  upsertMyApp,
  useMyApps,
} from "../myAppsStore";
import { groupByApp } from "../grouping";
import { applyLayoutBy, readLayout } from "../homeLayout";
import { presetApps, presetDiscoverApps } from "../presetApps";
import {
  readDismissedPresets,
  writeDismissedPresets,
} from "../presetDismissal";
import { iconImageUrl } from "../computeClient";
import { appLinkProps, routeAfterAdd } from "../openApp";
import type { PersonalApp } from "../types";
import type { NavigateOptions } from "@/app/routing/useAppNavigation";
import {
  normalizeDiscoveryCatalogue,
  resolveDiscoverySelection,
} from "./discoveryCatalogue";
import {
  detailsNavigationTarget,
  discoveryNavigationTarget,
  parseDiscoverySelection,
} from "./discoveryUrl";
import { useDiscoveryCatalogue } from "./useDiscoveryCatalogue";
import { useCatalogueScroll } from "./useCatalogueScroll";
import { useDiscoverSelection } from "./useDiscoverSelection";
import { useDiscoverPublicApp } from "./useDiscoverPublicApp";
import { useDiscoverTrial } from "./useDiscoverTrial";
import { usePublicTrialClock } from "./usePublicTrialClock";
import PublicTrialExpired from "./PublicTrialExpired";
import AppSplash from "./AppSplash";
import SurfaceState from "./SurfaceState";
import { useOnlineStatus } from "./useOnlineStatus";
import { useReconnectRetry } from "./useReconnectRetry";
import { docsRoute } from "@/static-site/docsLocales";
import { publicTrialSeconds, trialStartLabel } from "@/app/publicTrialPolicy";
import { clearPendingLibraryAdd, consumePendingLibraryAdd, rememberPendingLibraryAdd } from "./pendingLibraryAdd";
import { ConfirmDialog } from "@/components/settings/SettingsUI";
import type {
  DiscoveryCardModel,
  DiscoverySelection,
  DiscoverySort,
  DiscoveryUrlState,
} from "./types";
import type { AccountAuthState } from "@/auth/accountTypes";
import AppWindowFrame from "./AppWindowFrame";
import AppLaunchFeedback, { useAppLaunchFeedback } from "./AppLaunchFeedback";
import { FeaturedRow, HeroAppCards, HeroDemoPanel, HeroVideoBackdrop, useWideScreen } from "./EditorialSlots";
import { exploreRows, featuredPolicyFor, featuresForView } from "./exploreRows";
import { useDiscoveryEditorial } from "./useDiscoveryEditorial";
import { activeAppFrame } from "./activeAppFrame";
import { AppHeaderActions, AppTitleDetails } from "./AppHeaderActions";
import { useLibraryFavorites } from "./useLibraryFavorites";
import DiscoverFeedCard from "./DiscoverFeedCard";
import DiscoveryFilters from "./DiscoveryFilters";
import DiscoveryAppliedFilters from "./DiscoveryAppliedFilters";
import { useDiscoverUsage } from "./useDiscoverUsage";
import {
  bumpDiscoverAffinity,
  readHiddenIds,
  submitDiscoverFeedback,
  topAffinityCategory,
  undoDiscoverFeedback,
  type DiscoverFeedback,
} from "./discoverFeedback";
import { useDiscoverExploration } from "./useDiscoverExploration";
import { RecordedPreviewProvider } from "./RecordedAppPreview";
import { heroVariant } from "./heroVariant";
import HeroHeadline from "./HeroHeadline";
import "./discovery.css";

const CapsuleStage = lazy(() => import("./CapsuleStage"));
const PreviewPlayer = lazy(() => import("@/components/runs/PreviewPlayer"));

function selectionKey(selection: DiscoverySelection | null) {
  return selection ? `${selection.kind}:${selection.id}` : "";
}
/** Device-local preference: the Discover hero can be collapsed out of the way. */
export const DISCOVER_HERO_HIDDEN_KEY = "ato.discovery.hero.hidden.v1";

function readHeroHidden(): boolean {
  try {
    return localStorage.getItem(DISCOVER_HERO_HIDDEN_KEY) === "1";
  } catch {
    // Storage can be unavailable (private mode, blocked cookies); showing the
    // hero is the safe default.
    return false;
  }
}

function previewItem(entry: DiscoveryCardModel): RunFeedItem {
  return {
    id: entry.selection.id,
    slug: entry.selection.id,
    ref: entry.selection.id,
    name: entry.title,
    shortDescription: entry.description,
    iconUrl: entry.iconUrl ?? "",
    coverUrl: entry.thumbnailUrl ?? "",
    ownerHandle: "",
    ownerDisplay: "",
    ownerAvatarUrl: "",
    sourceUrl: "",
    postedAt: entry.publishedAt,
    surfaces: ["web_ui"],
    category: "open_source",
    tags: [],
    isFrontendOnly: false,
    runs: null,
    vouches: null,
  };
}

export default function DiscoveryWorkspace({
  urlState: routeState,
  accountState = { status: "loading" },
  onAccountRefresh,
  userId,
  onSignIn,
  onNavigate,
}: {
  urlState: DiscoveryUrlState;
  accountState?: AccountAuthState;
  onAccountRefresh?: () => Promise<void> | void;
  userId: string | null;
  onSignIn: () => void;
  onNavigate: (route: string, opts?: NavigateOptions) => void;
}) {
  const locale = useLocale();
  // The URL says which page this is: `/?c=` is Preview, `/?view=discover` is
  // the Grid. Back in the Grid the last Preview app stays selected (and its
  // session alive behind it) so Back/Forward or Discover → app resumes it.
  const retainedSelection = useRef(routeState.selection);
  if (routeState.selection) retainedSelection.current = routeState.selection;
  // An explicit Preview without an app (`style=preview`) keeps choosing the
  // default app instead.
  const unselectedPreview = new URLSearchParams(window.location.search).get("style") === "preview";
  const urlState = useMemo<DiscoveryUrlState>(
    () => routeState.selection || !retainedSelection.current || unselectedPreview
      ? routeState
      : { ...routeState, selection: retainedSelection.current },
    [routeState, unselectedPreview],
  );
  const previewRoute = Boolean(routeState.selection);
  useEffect(() => {
    const frame = requestAnimationFrame(() => recordStartup(appStartup(), "shell"));
    return () => cancelAnimationFrame(frame);
  }, []);
  const gpuPlacement = useGpuPlacement();
  const online = useOnlineStatus();
  const [dismissed, setDismissed] = useState(readDismissedPresets);
  const library = useMyApps();
  const verifiedUserId = accountState.status === "signed_in" ? userId : null;
  const favorites = useLibraryFavorites(verifiedUserId);
  const search = useSearch();
  // The order is URL state (`sort`) like the query and category, so a reload,
  // a shared link and Back/Forward keep it. Top rated is QA-only; elsewhere a
  // `sort=rating` link falls back to Recommended instead of an unrequested list.
  const sort: DiscoverySort = routeState.sort === "rating" && !qaVisible()
    ? "recommended"
    : routeState.sort ?? "recommended";
  const setSort = (next: DiscoverySort) =>
    onNavigate(discoveryNavigationTarget({ ...routeState, sort: next }));
  const signedIn = accountState.status === "signed_in" && !!userId;
  // Hidden rows never render. Signed-in sessions already have the server's
  // exclusion; the local copy covers anonymous visitors and keeps an
  // optimistic removal from flashing back while a request is in flight.
  const [localHidden] = useState<ReadonlySet<string>>(() => new Set(readHiddenIds()));
  const [removedCards, setRemovedCards] = useState<ReadonlyMap<string, DiscoverFeedback>>(
    () => new Map(),
  );
  const [feedbackNotice, setFeedbackNotice] = useState<{
    id: string;
    title: string;
    action: DiscoverFeedback;
  } | null>(null);
  const panels = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLElement>(null);
  const feed = useRef<HTMLElement>(null);
  const scrollPositions = useRef({ play: 0, list: 0 });
  const launchIntent = history.state?.atoLaunchIntent;
  const pendingDetailsLaunch = Boolean(
    launchIntent &&
      urlState.selection &&
      launchIntent.target === selectionKey(urlState.selection),
  );
  const { view, changeView, switching, viewError } = useDiscoveryView(stage, {
    skipGridGuard: pendingDetailsLaunch,
  });
  const detailOnlyArrival = useRef(
    new URLSearchParams(window.location.search).has("detail") && !urlState.selection,
  );
  // Changing presentation must retain the same selected publication/frame.
  const startedDefault = useRef(view === "play");
  if (view === "play") startedDefault.current = true;
  // For You only chooses a category: the server keeps the deterministic
  // Recommended order. Without a profile (or with personalization off) the
  // request is plain Recommended, and an empty category falls back the same way.
  const [forYouFellBack, setForYouFellBack] = useState(false);
  const forYouCategory = sort === "for_you" && urlState.themeId === null
    ? topAffinityCategory()
    : null;
  const serverSort = sort === "for_you" ? "recommended" : sort;
  const catalogueCategory = urlState.themeId
    ?? (sort === "for_you" && !forYouFellBack ? forYouCategory : null);
  const catalogue = useDiscoveryCatalogue(userId, dismissed, {
    authStatus: accountState.status, query: search.debouncedQuery,
    category: catalogueCategory, sort: serverSort, limit: 12,
    enabled: true,
  });
  useEffect(() => {
    setForYouFellBack(false);
  }, [sort, urlState.themeId]);
  useEffect(() => {
    if (sort !== "for_you" || forYouFellBack) return;
    if (!forYouCategory || urlState.themeId) return;
    // "No results for that category" is the documented fallback, not an
    // error: ask again for plain Recommended.
    if (catalogue.status === "ready" && catalogue.entries.length === 0)
      setForYouFellBack(true);
  }, [sort, forYouFellBack, forYouCategory, urlState.themeId, catalogue.status, catalogue.entries.length]);
  // A verified public deployment needs no admission call: its URL is already
  // in the catalogue, and the listing projection only returns active ones.
  const staticPreviewUrl = urlState.selection?.kind === "discover"
    ? catalogue.entries.find((entry) =>
        entry.selection.kind === "discover" &&
        entry.selection.id === urlState.selection!.id)?.previewUrl ?? null
    : null;
  const selected = useDiscoverSelection(
    urlState.selection,
    accountState,
    userId,
    startedDefault.current && !detailOnlyArrival.current,
    { staticPreviewUrl },
  );
  const selectedEntries = useMemo(() => normalizeDiscoveryCatalogue(selected.app ? [selected.app] : [], []), [selected.app]);
  const requestedSelection = urlState.selection ?? selectedEntries[0]?.selection ?? null;
  const neighborhoodScope = JSON.stringify([userId, accountState.status, search.debouncedQuery, urlState.themeId, sort]);
  // Keep one bounded browsing window while moving through its recommendations.
  // Excluding each newly selected app would reorder the feed and send Next back
  // to its first item. A new filter/account or an outside selection starts a window.
  const neighborhood = useRef<{
    scope: string;
    anchor: DiscoverySelection | null;
    card: DiscoveryCardModel | null;
    keys: Set<string>;
  }>({ scope: neighborhoodScope, anchor: requestedSelection, card: selectedEntries[0] ?? null, keys: new Set() });
  if (neighborhood.current.scope !== neighborhoodScope ||
      (requestedSelection && neighborhood.current.anchor && selectionKey(neighborhood.current.anchor) !== selectionKey(requestedSelection) &&
       !neighborhood.current.keys.has(selectionKey(requestedSelection)))) {
    neighborhood.current = { scope: neighborhoodScope, anchor: requestedSelection, card: selectedEntries[0] ?? null, keys: new Set() };
  }
  if (selectedEntries[0] && (!neighborhood.current.anchor || selectionKey(selectedEntries[0].selection) === selectionKey(neighborhood.current.anchor))) {
    neighborhood.current.card = selectedEntries[0];
  }
  const moreButton = useCatalogueScroll(
    view === "list" && catalogue.status === "ready" && catalogue.hasMore && !catalogue.loadingMore && !catalogue.error,
    catalogue.loadMore,
  );
  const updateApp = useCallback((id: string, update: (app: DiscoverApp) => DiscoverApp) => {
    catalogue.updateApp(id, update);
    selected.updateApp(id, update);
  }, [catalogue.updateApp, selected.updateApp]);
  const refresh = useCallback(() => { catalogue.refresh(); selected.refresh(); }, [catalogue.refresh, selected.refresh]);

  useLayoutEffect(() => {
    if (feed.current) feed.current.scrollTop = scrollPositions.current[view];
  }, [view]);
  const panelState = useDiscoveryPanels()!;
  const {
    panel: sheet,
    setPanel: setSheet,
    setWorkspaceActive,
    setWorkspaceView,
  } = panelState;
  useLayoutEffect(() => {
    setWorkspaceView(view);
    return () => setWorkspaceView(null);
  }, [view, setWorkspaceView]);
  // Library rail (the workspace's left sidebar) is opt-in and off by default;
  // see isDiscoveryLibraryRailEnabled. The header's Discover/Library tabs are
  // the navigation while it is away.
  const showLibraryRail = isDiscoveryLibraryRailEnabled();
  useEffect(() => {
    if (view === "list" && sheet === "discover") setSheet(null);
  }, [view, sheet, setSheet]);
  useEffect(() => {
    // A "library" panel restored from storage has no rail to open without the
    // flag; clearing it avoids leaving only the sheet shade behind.
    if (!showLibraryRail && sheet === "library") setSheet(null);
  }, [sheet, setSheet, showLibraryRail]);
  useEffect(() => {
    setWorkspaceActive(true);
    return () => {
      setWorkspaceActive(false);
      setSheet(null);
    };
  }, [setWorkspaceActive, setSheet]);
  useEffect(() => {
    if (view === "play" && search.open && window.innerWidth < 900)
      setSheet("discover");
    if (search.open && window.innerWidth >= 900)
      feed.current?.querySelector<HTMLInputElement>(".discovery-sheet-search input")?.focus();
  }, [search.open, setSheet, view]);
  useEffect(() => {
    const resize = () => {
      if (sheet && window.innerWidth >= (sheet === "library" ? 1280 : 900))
        setSheet(null);
    };
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [sheet, setSheet]);
  function closePanel() {
    setSheet(null);
    search.closePalette();
  }
  const [heroHidden, setHeroHidden] = useState<boolean>(readHeroHidden);
  const [heroCopy] = useState(() => heroVariant());
  const toggleHero = () => {
    setHeroHidden((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(DISCOVER_HERO_HIDDEN_KEY, next ? "1" : "0");
      } catch {
        // Best-effort persistence only; the toggle still works for this view.
      }
      return next;
    });
  };
  const [enlarged, setEnlarged] = useState(false);
  const viewRef = useRef(view);
  viewRef.current = view;
  // Focus (Expand) is a history entry on the same URL, so the browser's Back
  // returns to Preview rather than leaving Discover. Each Focus has its own
  // token: an older entry that still carries one never counts as this Focus.
  const focusToken = useRef<string | null>(null);
  const enterFocus = () => {
    const token = `${Date.now()}:${Math.random().toString(36).slice(2)}`;
    focusToken.current = token;
    history.pushState({ ...history.state, atoDiscoverFocus: token }, "");
    setEnlarged(true);
  };
  const leaveFocus = useCallback(() => {
    setEnlarged(false);
    const token = focusToken.current;
    focusToken.current = null;
    if (token && history.state?.atoDiscoverFocus === token) history.back();
  }, []);
  /** Programmatic exits keep the entry but clear its meaning. */
  const dropFocus = useCallback(() => {
    if (focusToken.current && history.state?.atoDiscoverFocus === focusToken.current)
      history.replaceState({ ...history.state, atoDiscoverFocus: undefined }, "");
    focusToken.current = null;
    setEnlarged(false);
  }, []);
  useEffect(() => {
    // Back leaves Focus; Forward does not re-enter it (the app may have
    // changed since).
    const pop = () => {
      if (focusToken.current && history.state?.atoDiscoverFocus !== focusToken.current) {
        focusToken.current = null;
        setEnlarged(false);
      }
    };
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);
  const [launchOpening, setLaunchOpening] = useState(false);
  const changePresentation = useCallback(
    async (next: "play" | "list", options?: { allowUncooperative?: boolean }) => {
      if (next === "list") {
        dropFocus();
        setSheet(null);
      }
      return changeView(next, options);
    },
    [changeView, setSheet, dropFocus],
  );
  const [surfaceDiscarded, setSurfaceDiscarded] = useState(false);
  const [launchAttempt, setLaunchAttempt] = useState(0);
  const [openingCard, setOpeningCard] = useState<{ userId: string | null; card: DiscoveryCardModel } | null>(null);
  // The tile/card that requested the launch. Seeding the Splash from it means
  // the first surface frame already has the app name and icon, before the
  // catalogue or admission for this selection has resolved.
  const [launchSeed, setLaunchSeed] = useState<{ key: string; title: string; iconUrl?: string | null } | null>(null);
  const staticPreviewRef = useRef(false);
  /** Discover / Back to grid: the Grid page, with the panel's category,
   *  query and sort; scroll positions are kept by this mounted workspace. */
  const returnToGrid = () => {
    onNavigate(discoveryNavigationTarget({ selection: null, query: routeState.query, themeId: routeState.themeId, sort: routeState.sort }));
  };
  const showGridView = async () => {
    // A failed display has no frame to pause; leaving it is an explicit exit.
    if (trial.state.kind === "ready" && !activeAppFrame(stage.current)) trial.cancelStart();
    // A static public document cannot acknowledge the audio-pause protocol.
    // Park it (hide the mounted iframe) instead of discarding the document.
    if (await changePresentation("list", {
      allowUncooperative: staticPreviewRef.current,
    })) return;
    // A trial/Hosted document that cannot pause must be stopped before hiding
    // the stage; this is separate from passive static retention.
    setSurfaceDiscarded(true);
    trial.cancelStart();
  };
  // The page follows the URL, including Back/Forward and direct visits.
  const showGridViewRef = useRef(showGridView);
  showGridViewRef.current = showGridView;
  useEffect(() => {
    if (previewRoute && viewRef.current === "list") void changePresentation("play");
    else if (!previewRoute && viewRef.current === "play" && new URLSearchParams(window.location.search).get("style") !== "preview")
      void showGridViewRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRoute]);
  const returnToGridRef = useRef(returnToGrid);
  returnToGridRef.current = returnToGrid;
  const { setShowGrid } = panelState;
  useEffect(() => {
    setShowGrid(() => void returnToGridRef.current());
    return () => setShowGrid(null);
  }, [setShowGrid]);
  useLayoutEffect(() => {
    if (surfaceDiscarded && view === "play") void changePresentation("list");
  }, [surfaceDiscarded, view, changePresentation]);
  const [pendingLike, setPendingLike] = useState<Set<string>>(new Set());
  const likeLocks = useRef(new Set<string>());
  const [pendingTry, setPendingTryState] = useState<{
    id: string;
    userId: string | null;
    selected: boolean;
  } | null>(null);
  const pendingTryRef = useRef<typeof pendingTry>(null);
  const setPendingTry = useCallback((value: typeof pendingTry) => {
    // Consume synchronously before start publishes an external-store update.
    pendingTryRef.current = value;
    setPendingTryState(value);
  }, []);
  const [adding, setAdding] = useState(false);
  // A failed save keeps its reason on the bookmark until the next attempt;
  // a successful one fills the bookmark and the toast says where it went.
  const [installResult, setInstallResult] = useState<{ id: string; status: "failed"; message?: string } | null>(null);
  const [installToast, setInstallToast] = useState<{ title: string; instanceId: string } | null>(null);
  useEffect(() => {
    if (!installToast) return;
    const timer = window.setTimeout(() => setInstallToast(null), 6000);
    return () => window.clearTimeout(timer);
  }, [installToast]);
  const addLock = useRef(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 5000);
    return () => window.clearTimeout(timer);
  }, [feedback]);
  const [error, setError] = useState<string | null>(null);
  const [resolvedUrl, setResolvedUrl] = useState<{
    id: string;
    url: string;
  } | null>(null);
  const [capsuleIdentity, setCapsuleIdentity] = useState<{
    target: string;
    name: string;
    iconUrl: string | null;
  } | null>(null);
  const onCapsuleMetadata = useCallback(
    (target: string, name: string, iconUrl: string | null) =>
      setCapsuleIdentity({ target, name, iconUrl }),
    [],
  );
  const [layout] = useState(readLayout);
  const selection = urlState.selection ?? (detailOnlyArrival.current ? null
    : selectedEntries[0]?.selection ?? (view === "list" ? catalogue.entries[0]?.selection ?? null : null));
  // Grid may show catalogue metadata before any app has been selected to run.
  const mayOpenSurface = startedDefault.current || Boolean(urlState.selection);
  const key = selectionKey(selection);
  const currentRef = useRef({ userId, key });
  currentRef.current = { userId, key };
  const availableEntries = urlState.selection?.kind === "discover" ? selectedEntries
    : [...selectedEntries, ...catalogue.entries, ...normalizeDiscoveryCatalogue([], presetDiscoverApps(dismissed))];
  const resolved = resolveDiscoverySelection(selection, availableEntries, {
    authState: accountState.status === "signed_in" ? "verified" : accountState.status === "signed_out" ? "signed_out" : "pending",
    userId: userId ?? "",
    snapshot: library,
  });
  // A static preview skips admission, so the selected row is resolved from
  // the catalogue that supplied the verified URL. Other selections keep the
  // existing resolution (selected detail first).
  const previewEntry = staticPreviewUrl
    ? catalogue.entries.find((candidate) =>
        selectionKey(candidate.selection) === selectionKey(selection)) ?? null
    : null;
  const entry = resolved?.kind === "catalogue" ? resolved.entry : previewEntry;
  // Card metadata paints immediately; admission still comes exclusively from
  // the selected publication endpoint, never this presentation-only hint.
  const presentationEntry = entry ?? (openingCard?.userId === userId && selectionKey(openingCard.card.selection) === key ? openingCard.card : null)
    ?? catalogue.entries.find(card => selectionKey(card.selection) === key) ?? null;
  // Discover always opens a disposable/public experience. Only a deliberate
  // library selection may resolve an owned Instance, including after Try ends.
  const [resolvedInstance, setResolvedInstance] = useState<{ userId: string | null; app: PersonalApp } | null>(null);
  // A failed Library launch is an error state, not an endless Splash: the
  // Instance component draws its own alert once the surface releases it.
  const [instanceFailure, setInstanceFailure] = useState<string | null>(null);
  const instance = accountState.status === "signed_in" && resolvedInstance?.userId === userId && resolvedInstance.app.instance_id === selection?.id
    ? resolvedInstance.app : resolved?.kind === "instance" ? resolved.instance : null;
  const trial = useDiscoverTrial({
    listingId: entry?.selection.kind === "discover" ? entry.selection.id : null,
    userId,
    canTry: entry?.publicTryAvailable ?? false,
    allowPublic: entry?.publicAccessAvailable === true,
    resolveGpuPlacement: gpuPlacement.choose,
    onSignIn,
  });
  useEffect(() => {
    if (!pendingTry || pendingTryRef.current !== pendingTry) return;
    if (
      pendingTry.userId !== userId ||
      (pendingTry.selected && entry?.selection.id !== pendingTry.id)
    ) {
      setPendingTry(null);
      return;
    }
    if (entry?.selection.id === pendingTry.id && !trial.busy) {
      setPendingTry(null);
      trial.start();
    } else if (entry?.selection.id === pendingTry.id && !pendingTry.selected) {
      setPendingTry({ ...pendingTry, selected: true });
    }
  }, [pendingTry, userId, entry?.selection.id, trial.busy]);
  useLayoutEffect(() => {
    const intent = launchIntent;
    if (!intent || intent.target !== key || !entry) return;
    // Opening from the details modal must land on the running surface, not
    // start a Try invisibly behind the Grid. The modal navigates without
    // changing the persisted Discover view, so make that transition here.
    if (view !== "play") {
      setLaunchOpening(true);
      void changePresentation("play").then((opened) => {
        if (!opened) { setLaunchOpening(false); return; }
        if (history.state?.atoLaunchIntent?.target !== intent.target) return;
        history.replaceState(
          { ...history.state, atoLaunchIntent: undefined },
          "",
        );
        if (entry.publicTryAvailable)
          setPendingTry({ id: entry.selection.id, userId, selected: true });
      });
      return;
    }
    if (trial.busy) return;
    history.replaceState({ ...history.state, atoLaunchIntent: undefined }, "");
    if (entry.publicTryAvailable)
      setPendingTry({ id: entry.selection.id, userId, selected: true });
  }, [launchIntent, key, entry, trial.busy, view, changePresentation]);
  const themes = catalogue.categories;
  // Measurement records which list produced an impression. A category filter
  // wins over the ordering because that is what narrowed the result set.
  const measurementView: DiscoverMeasurementView =
    sort === "for_you" ? "for_you"
      : sort === "popular" ? "popular"
        : sort === "new" ? "new"
          : urlState.themeId ? "category"
            : "all";
  const suppressedIds = new Set(removedCards.keys());
  if (!signedIn) for (const id of localHidden) suppressedIds.add(id);
  const isDismissed = (card: DiscoveryCardModel) =>
    card.selection.kind === "discover" && suppressedIds.has(card.selection.id);
  const navigationResults = catalogue.entries.filter(card => !isDismissed(card));
  const cards = view === "play" ? navigationResults.filter(card => selectionKey(card.selection) !== key) : navigationResults;
  const explorationCard = useDiscoverExploration({
    enabled: sort === "for_you" && view === "list",
    displayedIds: new Set(cards.flatMap(card => card.selection.kind === "discover" ? [card.selection.id] : [])),
    hiddenIds: suppressedIds,
  });
  // The exploration row obeys the same dismissal as the paged list.
  const exploration = explorationCard && !isDismissed(explorationCard)
    ? explorationCard
    : null;
  // Exploration sits after the third card and is never part of the paged
  // list, so it cannot shift next_cursor.
  const displayCards = useMemo(() => {
    if (!exploration || exploration.selection.kind !== "discover") return cards;
    const id = exploration.selection.id;
    const withoutExploration = cards.filter(card =>
      !(card.selection.kind === "discover" && card.selection.id === id));
    const at = Math.min(3, withoutExploration.length);
    return [...withoutExploration.slice(0, at), exploration, ...withoutExploration.slice(at)];
  }, [cards, exploration]);
  // The row goes at once, and the request only persists the choice; a failure
  // must not put a card the person dismissed back in front of them.
  const dismissCard = useCallback((card: DiscoveryCardModel, action: DiscoverFeedback) => {
    if (card.selection.kind !== "discover") return;
    const id = card.selection.id;
    setRemovedCards(previous => new Map(previous).set(id, action));
    setFeedbackNotice({ id, title: card.title, action });
    if (action === "helpful") bumpDiscoverAffinity(card.category);
    void submitDiscoverFeedback({ appId: id, title: card.title, action, signedIn });
  }, [signedIn]);
  const undoFeedback = useCallback(() => {
    const notice = feedbackNotice;
    if (!notice) return;
    setFeedbackNotice(null);
    setRemovedCards(previous => {
      const next = new Map(previous);
      next.delete(notice.id);
      return next;
    });
    void undoDiscoverFeedback({ appId: notice.id, action: notice.action, signedIn });
  }, [feedbackNotice, signedIn]);
  const wideScreen = useWideScreen();
  const editorial = useDiscoveryEditorial(catalogue.entries, view === "list" && catalogue.status !== "loading", { heroCards: wideScreen });
  // Featured placement follows the grid's real column count (measured before
  // paint, so rows never jump after they appear).
  const [cardGrid, setCardGrid] = useState<HTMLDivElement | null>(null);
  const [gridColumns, setGridColumns] = useState(4);
  useLayoutEffect(() => {
    if (!cardGrid) return;
    const measure = () => {
      // "none" (not a grid yet, or no layout) keeps the last known count.
      const tracks = getComputedStyle(cardGrid).gridTemplateColumns;
      if (!tracks || tracks === "none") return;
      setGridColumns(tracks.split(" ").filter(Boolean).length);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(cardGrid);
    return () => observer.disconnect();
  }, [cardGrid]);
  // The Featured row joins the recommended grid only (see featuresForView).
  const features = view === "list" && sort === "recommended"
    ? featuresForView(editorial.spotlights, { query: search.query || urlState.query, category: urlState.themeId })
    : [];
  const anchor = neighborhood.current.card;
  const navigationCards = view === "play" && anchor
    && !navigationResults.some(card => selectionKey(card.selection) === selectionKey(anchor.selection))
    ? [anchor, ...navigationResults]
    : navigationResults;
  neighborhood.current.keys = new Set(navigationCards.map(card => selectionKey(card.selection)));
  // Known catalogue values win over a placeholder: the window/header may show
  // "App" before anything is admitted, but the Splash (which freezes its name
  // for the launch) must not change its copy mid-launch.
  const intentSelection = launchIntent ? parseDiscoverySelection(launchIntent.target) : null;
  const intentCard = intentSelection
    ? [...selectedEntries, ...catalogue.entries].find(candidate => selectionKey(candidate.selection) === selectionKey(intentSelection))
      ?? (openingCard && selectionKey(openingCard.card.selection) === selectionKey(intentSelection) ? openingCard.card : null)
    : null;
  // A navigation may remount this workspace between the click and the surface
  // render. The history entry keeps the tile seed available for that first
  // frame; the React state covers the same-instance path.
  const historySeed = (history.state as { atoLaunchSeed?: { key: string; title: string; iconUrl?: string | null } } | null)?.atoLaunchSeed;
  const activeSeed = launchSeed && launchSeed.key === key ? launchSeed
    : historySeed && historySeed.key === key ? historySeed : null;
  const seededTitle = activeSeed?.title
    ?? (intentCard && selectionKey(intentSelection) === key ? intentCard.title : null);
  const seededIconUrl = activeSeed?.iconUrl ?? intentCard?.iconUrl ?? null;
  const knownTitle =
    instance?.display_name ??
    presentationEntry?.title ??
    entry?.title ??
    seededTitle ??
    (selection?.kind === "capsule"
      ? capsuleIdentity?.target === selection.id
        ? capsuleIdentity.name
        : selection.id
      : null);
  const title = knownTitle ?? t("App");
  // A published static deployment IS the App: open it directly instead of
  // allocating a Try Instance (which would also create D1 rows per visitor).
  const publicApp = useDiscoverPublicApp(
    mayOpenSurface && entry?.selection.kind === "discover" &&
      entry.executionClass === "Browser" &&
      entry.publicAccessAvailable &&
      !entry.previewUrl
      ? entry.selection.id
      : null,
    entry?.publishedVersion ?? null,
    selected.app?.id === entry?.selection.id ? selected.admission : undefined,
  );
  const previewUrl = !mayOpenSurface ? null :
    entry?.selection.kind === "preset"
      ? entry.externalUrl
      : entry?.previewUrl ?? publicApp.appUrl;
  staticPreviewRef.current = Boolean(previewUrl) && !publicApp.instanceId;
  // A public static deployment has no Try to count down. Only an admission
  // that carries a server expiry starts the clock; a null trial limit (a
  // client-side Browser preview) leaves it idle while the credential renews.
  const publicClock = usePublicTrialClock(
    // A retry is a new admission: the clock must not keep the previous
    // admission's expired deadline (the server's expiry still caps it).
    `${key}:${entry?.publishedVersion ?? ""}:${launchAttempt}`,
    Boolean(previewUrl) && Boolean(publicApp.expiresAt) && selection?.kind !== "instance",
    publicApp.expiresAt,
    publicApp.trialLimitSeconds !== undefined ? publicApp.trialLimitSeconds : publicTrialSeconds(entry?.publicTrialPolicy),
  );
  const publicTrialExpired = publicClock.expired || trial.expired;
  const frameRetainedApp = previewUrl
    ? publicApp.instanceId && publicApp.expiresAt
      ? {
          key,
          identity: `${key}:${entry?.publishedVersion ?? "unknown"}:${publicApp.instanceId}:${publicClock.limitSeconds ?? "unlimited"}:${launchAttempt}`,
          url: previewUrl,
          title,
          // An unlimited client-side preview has no trial deadline: the served
          // app renews its credential while open, so the frame must not be
          // pinned to the first lease. A bounded trial keeps its own deadline.
          expiresAt: publicClock.limitSeconds === null ? null : publicApp.expiresAt,
          cooperative: true,
        }
      : {
          key,
          identity: `${key}:${entry?.publishedVersion ?? "unknown"}:static`,
          url: previewUrl,
          title,
          expiresAt: null,
          cooperative: false,
        }
    : null;
  const accountScope = accountState.status === "signed_in" && userId
    ? userId : accountState.status === "signed_out" ? "guest" : null;
  const lastAccountScope = useRef(accountScope);
  const [frameScope, setFrameScope] = useState({ epoch: 0, previousUrl: null as string | null });
  useLayoutEffect(() => {
    if (accountScope === null) return;
    const previous = lastAccountScope.current;
    lastAccountScope.current = accountScope;
    // Initial auth resolution does not invalidate a public guest admission.
    // A real account transition discards retained documents and obtains a new
    // one-time credential before mounting anything in the new scope.
    if (previous !== null && previous !== accountScope) {
      setFrameScope(value => ({ epoch: value.epoch + 1, previousUrl: previewUrl ?? null }));
      if (publicApp.instanceId) selected.retry();
    }
  }, [accountScope, previewUrl, publicApp.instanceId, selected.retry]);
  const onUsageCount = useCallback((id: string, count: number) => {
    updateApp(id, app => app.stats ? { ...app, stats: { ...app.stats,
      discover_run_count: Math.max(app.stats.discover_run_count ?? 0, count) } } : app);
  }, [updateApp]);
  const usage = useDiscoverUsage({ entry, visible: view === "play", selectionIsDiscover: selection?.kind === "discover",
    appUrl: publicTrialExpired ? null : previewUrl ?? trial.instance?.app_url ?? null,
    surface: entry?.previewUrl ? { kind: "static" }
      : publicApp.instanceId ? { kind: "browser", instance_id: publicApp.instanceId }
      : trial.state.kind === "ready" && trial.instance ? { kind: "hosted", instance_id: trial.instance.instance_id } : null,
    onCount: onUsageCount,
  });
  const externalUrl = instance
    ? resolvedUrl?.id === instance.instance_id
      ? resolvedUrl.url
      : null
    : trial.instance?.app_url
      ? (() => {
          const url = new URL(trial.instance.app_url);
          if (trial.instance.public_guest_try)
            url.searchParams.delete("app_view_token");
          return url.href;
        })()
      : publicApp.appUrl
        ? (() => {
            const url = new URL(publicApp.appUrl);
            url.searchParams.delete("app_view_token");
            return url.href;
          })()
        : previewUrl;
  // A details-modal launch arrives while Grid is still the view. Do not mount
  // an app frame into the Grid pause guard: switch first, then render the app.
  const launchViewPending = launchOpening || Boolean(
    launchIntent && launchIntent.target === key && view !== "play",
  );
  // A hosted Try may spend a long time allocating and verifying. That is not
  // a failed load: the player already shows the preparation stage. Start the
  // frame-load deadline only once the app URL actually exists.
  const launchActive = view === "play" && !surfaceDiscarded && !publicTrialExpired && (
    Boolean(previewUrl) || trial.state.kind === "ready"
  );
  const launchFeedback = useAppLaunchFeedback(
    `${accountScope}:${selectionKey(urlState.selection) || "default"}:${frameScope.epoch}:${launchAttempt}`,
    launchActive,
    Boolean(selected.error || publicApp.error || trial.state.kind === "error"),
    () => {
      dropFocus();
      setPendingTry(null);
      if (history.state?.atoLaunchIntent?.target === key)
        history.replaceState({ ...history.state, atoLaunchIntent: undefined }, "");
      // A frame timeout is not a failed allocation. Keep a verified Try and
      // its work available for display retry or its own tab until Stop/expiry.
      if (trial.state.kind !== "ready") trial.cancelStart();
    },
    previewUrl ?? trial.instance?.app_url ?? (instance ? externalUrl : null),
  );
  const surfaceReady = useCallback((url: string, bridge?: boolean) => {
    usage.onSurfaceReady(url, bridge);
    const expected = previewUrl ?? trial.instance?.app_url ?? (selection?.kind === "capsule" ? activeAppFrame(stage.current)?.src : null);
    const frame = activeAppFrame(stage.current);
    if (expected && frame?.src === expected && new URL(url).origin === new URL(expected).origin) {
      // The overlay leaves here, whether the app arrived through the bridge
      // or the frame's load event: this is the first visible moment.
      recordStartup(appStartup(), "frame-revealed");
      launchFeedback.ready();
    }
  }, [usage.onSurfaceReady, previewUrl, trial.instance?.app_url, selection?.kind, launchFeedback.ready]);
  // Three distinct moments: selection (click), first paint (what the visitor
  // can actually see and touch) and load (every resource finished). Keeping
  // them separate is what makes "ten seconds to open" measurable and honest.
  const revealFrame = useCallback(() => {
    recordStartup(appStartup(), "frame-revealed");
    launchFeedback.reveal();
  }, [launchFeedback.reveal]);
  // ── One launch condition for the whole surface ────────────────────────────
  // From the launch request until the app is *operable* (existing readiness,
  // not the iframe's load event alone) the surface shows exactly one Default
  // Splash plus the header progress bar. The internal phases (queued →
  // verified → first paint) only feed this boolean; they never pick a
  // different loading component.
  const launchOperable = launchFeedback.phase === "ready";
  const surfaceLoadingPhase = launchFeedback.phase === "loading" || launchFeedback.phase === "slow" || launchFeedback.phase === "revealed";
  const trialStarting = trial.state.kind === "queued" || trial.state.kind === "starting";
  // A public Browser app is admitted before it has any URL to frame.
  const admissionPending = view === "play" && entry?.selection.kind === "discover" &&
    entry.executionClass === "Browser" && entry.publicAccessAvailable === true &&
    !previewUrl && !publicApp.error;
  // Library launches resolve their Instance before offering the stable URL.
  const instancePending = view === "play" && selection?.kind === "instance" &&
    accountState.status === "signed_in" && !launchOperable &&
    instanceFailure !== (selection?.id ?? "");
  const launchBlocked = launchFeedback.phase === "failed" || trial.state.kind === "error" ||
    publicTrialExpired || surfaceDiscarded || Boolean(selected.error) || Boolean(publicApp.error) ||
    (selection?.kind === "instance" && instanceFailure === selection.id);
  // The listing's own admission is still pending (the selected app's point
  // document has not resolved yet). There is nothing usable to show behind a
  // Run control, so the same Splash owns this moment.
  const selectionPending = view === "play" && selected.status === "loading";
  // Readiness alone is not enough to uncover the surface: the selected app's
  // metadata must be resolved AND something of the app must actually be
  // mounted. Otherwise a ready signal that arrives first would reveal a blank
  // stage or a bare "Loading app…" line underneath.
  const appSurfaceMounted = Boolean(previewUrl) || trial.state.kind === "ready" ||
    Boolean(instance) || Boolean(resolvedInstance) ||
    (selection?.kind === "capsule" && Boolean(entry || capsuleIdentity));
  const launchFinished = launchOperable && !selectionPending && appSurfaceMounted;
  const launchSignals = !launchViewPending &&
    (trialStarting || surfaceLoadingPhase || admissionPending || instancePending || selectionPending);
  // Ready but not yet finished keeps the Splash: this is the PdfCraft race
  // where readiness lands before the selection resolves and the app surface
  // mounts.
  const launching = view === "play" && !launchBlocked && online && !launchFinished &&
    (launchSignals || launchOperable);
  // Cancellation stays reachable while the launch is blocked on the network,
  // but the loading Splash itself is not the place for it.
  const launchInProgress = view === "play" && !launchBlocked && (launchSignals || launchViewPending || (launchOperable && !launchFinished));
  const splashIcon = selection && !selected.failure?.notFound ? (
    <AppIcon
      name={title}
      seed={instance?.compute_id ?? selection.id}
      size={64}
      color={instance?.icon_color}
      glyph={instance?.icon_glyph}
      imageUrl={iconImageUrl(instance?.icon_image_path ?? null)}
      listingIconUrl={
        instance?.listing_icon_url ??
        entry?.iconUrl ??
        seededIconUrl ??
        (selection.kind === "capsule" && capsuleIdentity?.target === selection.id
          ? capsuleIdentity.iconUrl
          : null)
      }
    />
  ) : undefined;
  // Freeze the Splash's name for the whole launch. A title that only arrives
  // after the launch starts must not change the body from "App" (or nothing)
  // to the real name; an unknown name simply stays unnamed for this launch.
  // The window shell is mounted during the Grid → Surface transition, but the
  // Grid's stage is invisible. Render the Splash only once the surface is the
  // visible one, so a sampler (and a person) never sees an empty/offscreen
  // Splash before the first real frame.
  const surfaceVisible = view === "play";
  const splashActive = surfaceVisible && (launching || launchViewPending);
  const splashNameRef = useRef<string | null>(null);
  if (!splashActive) splashNameRef.current = null;
  else if (splashNameRef.current === null && knownTitle) splashNameRef.current = knownTitle;
  const splashName = splashNameRef.current;
  const retryLaunch = () => {
    if ((trial.failure?.retryAt ?? selected.failure?.retryAt ?? 0) > Date.now()) return;
    setSurfaceDiscarded(false);
    setLaunchAttempt(value => value + 1);
    if (entry?.publicTryAvailable && entry.executionClass !== "Browser") {
      // The allocation may already exist. Reconnect the ready surface instead
      // of asking the controller to start a second one.
      if (trial.state.kind === "ready") retrySurface();
      else if (!trial.busy) {
        setPendingTry(null);
        trial.start();
      }
    } else retrySurface();
  };
  // Reconnecting during a launch retries once, so the visitor does not have to
  // press Reconnect on a surface they already asked to open. A permanent
  // failure (the app is gone) is never retried by connectivity.
  useReconnectRetry(
    online,
    view === "play" && !launchFinished && !publicTrialExpired && !selected.failure?.notFound,
    retryLaunch,
  );
  /** One cancel for every pre-paint moment: abandon the allocation, then leave
   *  the surface. The controller's own owner fence settles a late POST. */
  const cancelLaunch = () => {
    setPendingTry(null);
    trial.cancelStart();
    // stop() is a no-op when nothing was allocated; when a Try is running it
    // releases the resources behind the surface we are leaving.
    void trial.stop();
    void returnToGrid();
  };
  /** A retry from the ended surface only exists while the controller can start
   *  again; the state machine keeps its own guard either way. */
  const retryExpiredTry = () => {
    if (trial.busy) return;
    retryLaunch();
  };
  const retryPossible = !trial.busy && (trial.failure?.retryAt ?? 0) <= Date.now();
  useEffect(() => {
    if (view === "play" && launchOpening) setLaunchOpening(false);
  }, [view, launchOpening]);
  const launchFailure = trial.failure?.message ?? selected.failure?.message ?? (trial.state.kind === "error"
    ? trial.state.retryable
      ? "The app didn't open. Try again, or go back to the app list."
      : "This app is currently unavailable. Go back to the app list."
    : selected.error || publicApp.error
      ? "The app didn't open. Try again, or go back to the app list."
      : entry?.publicTryAvailable || entry?.publicAccessAvailable
        ? "The app didn't open. Try again, or go back to the app list."
        : "This app is currently unavailable. Go back to the app list.");
  const launchRetryable = trial.state.kind === "error"
    ? trial.state.retryable
    : selected.failure ? selected.failure.retryable : Boolean(entry?.publicTryAvailable || entry?.publicAccessAvailable);
  const [signInPrompt, setSignInPrompt] = useState(false);
  /** The Discover listing being reported from the app window's menu. */
  const [reporting, setReporting] = useState<{ id: string; title: string } | null>(null);
  // A full-page sign-in destroys the running document. Say that before we
  // navigate, rather than replacing someone's game or note without warning.
  const requestSignIn = () => {
    if (!publicTrialExpired && (previewUrl || trial.state.kind === "ready")) {
      setSignInPrompt(true);
      return;
    }
    onSignIn();
  };
  const onResolved = useCallback(
    (app: PersonalApp) => {
      setResolvedUrl({ id: app.instance_id, url: app.app_url });
      setResolvedInstance({ userId: currentRef.current.userId, app });
      launchFeedback.ready();
    },
    [launchFeedback.ready],
  );
  const retrySurface = () => {
    setFrameScope(previous => ({ epoch: previous.epoch + 1, previousUrl: previewUrl }));
    // Reuse an immutable preview or a verified Try. Clearing selection would
    // rebind the Try controller to no listing and stop the saved work.
    if (entry?.previewUrl || trial.state.kind === "ready") return;
    if (entry?.executionClass === "Browser" && entry.publicAccessAvailable) publicApp.retry();
    else selected.retry();
  };
  const tiles = useMemo(() => {
    const presets = presetApps(dismissed).map((preset) => ({
      id: preset.id,
      name: preset.display_name,
      icon: preset.icon_url,
      selection: { kind: "preset", id: preset.id } as DiscoverySelection,
    }));
    const apps =
      verifiedUserId && library.userId === verifiedUserId
        ? groupByApp(library.instances ?? []).map((group) => ({
            id: group.compute_id,
            name: group.display_name,
            icon: group.listing_icon_url,
            selection: {
              kind: "instance",
              id: group.instances[0].instance_id,
            } as DiscoverySelection,
            group,
            href: group.instances[0].app_url,
          }))
        : [];
    return applyLayoutBy([...presets, ...apps], layout, (tile) => tile.id);
  }, [dismissed, verifiedUserId, library, layout]);
  const knownFavoriteTiles = [
    ...tiles,
    ...[...catalogue.entries, ...selectedEntries].map((card) => {
      const listing = selected.app?.id === card.selection.id
        ? selected.app : catalogue.apps.find(app => app.id === card.selection.id);
      const launchInstanceId = verifiedUserId && listing?.added ? listing.instance_id : null;
      const saved = launchInstanceId && library.userId === verifiedUserId
        ? library.instances?.find(app => app.instance_id === launchInstanceId && app.status === "active" && app.public_try_expires_at == null)
        : null;
      return {
        id: selectionKey(card.selection),
        name: card.title,
        icon: card.iconUrl,
        selection: card.selection,
        ...(saved ? { href: saved.app_url } : {}),
        ...(launchInstanceId ? { launchInstanceId } : {}),
      };
    }),
  ];
  const favoritesByKey = new Map(knownFavoriteTiles.map(tile => [selectionKey(tile.selection), tile]));
  const favoriteTiles = favorites.ids.flatMap(id => {
    const known = favoritesByKey.get(id);
    if (known) return [known];
    const target = parseDiscoverySelection(id);
    // A shortcut stays reachable when its metadata is outside the current
    // page. Its point endpoint validates availability when the user opens it.
    return target ? [{ id, name: target.id, icon: undefined, selection: target }] : [];
  });
  const summaryEntry =
    presentationEntry ??
    (instance
      ? (catalogue.entries.find((card) =>
          catalogue.apps.some(
            (app) =>
              app.id === card.selection.id &&
              app.instance_id === instance.instance_id,
          ),
        ) ?? null)
      : null);
  useEffect(() => {
    if (!verifiedUserId) return;
    adoptMyAppsUser(verifiedUserId);
    const refresh = () => {
      if (document.visibilityState !== "hidden") void refreshMyApps();
    };
    refresh();
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [verifiedUserId]);
  useEffect(() => {
    setError(null);
    setFeedback(null);
  }, [key, userId]);
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (document.querySelector("dialog.app-details-modal[open]")) return;
        setSheet(null);
        search.closePalette();
        leaveFocus();
      }
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [setSheet, search.closePalette, leaveFocus]);
  useEffect(() => {
    if (!sheet) return;
    const previous = document.activeElement as HTMLElement | null;
    const panel = panels.current?.querySelector<HTMLElement>(
      sheet === "library" ? ".discovery-rail" : ".discovery-feed",
    );
    const background = [
      document.querySelector<HTMLElement>(".gallery-header"),
      ...Array.from(
        panels.current?.querySelectorAll<HTMLElement>(
          ".discovery-stage, aside, .discovery-view-toolbar",
        ) ?? [],
      ),
    ].filter((node): node is HTMLElement => !!node && node !== panel);
    const inert = background.map((node) => node.inert);
    background.forEach((node) => {
      node.inert = true;
    });
    const focus = () => {
      const input = search.open
        ? panel?.querySelector<HTMLInputElement>(
            ".discovery-sheet-search input",
          )
        : null;
      (
        input ??
        panel?.querySelector<HTMLButtonElement>(".discovery-panel-close")
      )?.focus();
    };
    focus();
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !panel) return;
      const controls = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input, textarea, [tabindex="0"]',
        ),
      ).filter((control) => control.offsetParent !== null);
      const first = controls[0],
        last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", trapFocus);
    return () => {
      document.removeEventListener("keydown", trapFocus);
      background.forEach((node, index) => {
        node.inert = inert[index];
      });
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [sheet, search.open]);
  /** Seed the launch UI from the tile/card that requested it, so the first
   *  surface frame already names the app instead of flashing an empty Splash. */
  const seedLaunch = (target: DiscoverySelection) => {
    const targetKey = selectionKey(target);
    const card = [...selectedEntries, ...catalogue.entries].find(candidate => selectionKey(candidate.selection) === targetKey)
      ?? (openingCard && selectionKey(openingCard.card.selection) === targetKey ? openingCard.card : null);
    const tile = tiles.find(candidate => candidate.id === targetKey);
    const title = card?.title ?? tile?.name ?? (target.kind === "capsule" ? target.id : null);
    if (!title) return;
    const seed = { key: targetKey, title, iconUrl: card?.iconUrl ?? null };
    setLaunchSeed(seed);
    try {
      history.replaceState({ ...history.state, atoLaunchSeed: seed }, "");
    } catch {
      // History writes can fail in restricted contexts; the state seed still
      // covers the mounted component.
    }
  };
  const select = (next: DiscoverySelection, discoveryIntent?: NavigateOptions["discoveryIntent"]) => {
    seedLaunch(next);
    setPendingTry(null);
    if (selectionKey(next) !== key || !mayOpenSurface) {
      const target = discoveryNavigationTarget({ ...urlState, selection: next });
      if (discoveryIntent) onNavigate(target, { discoveryIntent });
      else onNavigate(target);
    } else if (discoveryIntent) {
      history.replaceState({ ...history.state, atoDiscoveryIntent: discoveryIntent }, "");
    }
    setSheet(null);
    search.closePalette();
  };
  const openDetails = (
    target: DiscoverySelection,
    tab: "overview" | "comments" | "reviews" = "overview",
  ) => {
    setSheet(null);
    search.closePalette();
    onNavigate(
      detailsNavigationTarget(
        target,
        tab,
        discoveryNavigationTarget(urlState),
      ),
    );
  };
  const tryCard = (card: DiscoveryCardModel) => {
    if (!card.publicTryAvailable) return;
    if (
      selectionKey(card.selection) === key &&
      (trial.busy || trial.state.kind === "ready")
    )
      return;
    select(card.selection, usage.begin(card));
    if (!userId && !card.publicAccessAvailable) {
      requestSignIn();
      return;
    }
    setPendingTry({ id: card.selection.id, userId, selected: false });
  };
  const openCard = async (card: DiscoveryCardModel) => {
    setOpeningCard({ userId, card });
    const seed = { key: selectionKey(card.selection), title: card.title, iconUrl: card.iconUrl ?? null };
    setLaunchSeed(seed);
    try {
      history.replaceState({ ...history.state, atoLaunchSeed: seed }, "");
    } catch {
      // Seeded for the first surface frame when possible; not essential.
    }
    setSurfaceDiscarded(false);
    if (launchFeedback.phase === "failed") setLaunchAttempt(value => value + 1);
    if(card.selection.kind==="discover"&&card.publishedVersion) measureDiscover(card.selection.id,card.publishedVersion,"detail");
    if (view === "list" && !(await changeView("play"))) return;
    if (
      !card.previewUrl &&
      !(card.publicAccessAvailable && card.executionClass === "Browser") &&
      card.publicTryAvailable
    ) {
      tryCard(card);
    } else {
      select(card.selection, usage.begin(card));
    }
  };

  const resetTrial = () => {
    if (!entry || trial.state.kind !== "ready") return;
    // Queue against the current account/selection. The existing controller
    // releases its handle only after resources_released; navigation cancels it.
    void trial.stop();
    setPendingTry({ id: entry.selection.id, userId, selected: true });
  };
  async function like(card: DiscoveryCardModel) {
    if (!userId) {
      requestSignIn();
      return;
    }
    if (
      card.selection.kind !== "discover" ||
      card.viewer?.canLike !== true ||
      likeLocks.current.has(card.selection.id)
    )
      return;
    const id = card.selection.id;
    const before = selected.app?.id === id ? selected.app : catalogue.apps.find((app) => app.id === id);
    if (!before?.stats || !before.viewer) return;
    likeLocks.current.add(id);
    setPendingLike(new Set(likeLocks.current));
    setError(null);
    const liked = !before.viewer.liked;
    updateApp(id, (app) => ({
      ...app,
      viewer: { ...before.viewer!, liked },
      stats: {
        ...before.stats!,
        like_count:
          before.stats!.like_count === null
            ? null
            : Math.max(0, before.stats!.like_count + (liked ? 1 : -1)),
      },
    }));
    try {
      const result = await discoverClient.setLike(id, liked);
      updateApp(id, (app) => ({
        ...app,
        viewer: { ...app.viewer!, liked: result.liked },
        stats: { ...app.stats!, like_count: result.like_count },
      }));
      if (currentRef.current.userId === userId) setFeedback(result.liked ? `${card.title}: liked publicly` : `${card.title}: like removed`);
    } catch (cause) {
      updateApp(id, (app) => ({
        ...app,
        viewer: before.viewer,
        stats: app.stats
          ? { ...app.stats, like_count: before.stats!.like_count }
          : before.stats,
      }));
      if (currentRef.current.userId === userId)
        setError(
          cause instanceof Error ? cause.message : t("The like could not save."),
        );
    } finally {
      likeLocks.current.delete(id);
      setPendingLike(new Set(likeLocks.current));
    }
  }
  async function add(card = entry) {
    if (!card) return;
    if (card.selection.kind === "preset") {
      const next = dismissed.filter((id) => id !== card.selection.id);
      writeDismissedPresets(next);
      setDismissed(next);
      return;
    }
    if (!userId) {
      rememberPendingLibraryAdd(card.selection.id);
      requestSignIn();
      return;
    }
    const listing = selected.app?.id === card.selection.id ? selected.app : catalogue.apps.find((app) => app.id === card.selection.id);
    if (listing?.added && listing.instance_id) {
      onNavigate(routeAfterAdd(listing.instance_id));
      return;
    }
    if (addLock.current) return;
    addLock.current = true;
    setAdding(true);
    setError(null);
    setInstallResult(null);
    try {
      const installed = await addDiscoverReadyApp(card.selection.id, undefined, gpuPlacement.choose);
      if (currentRef.current.userId !== userId) return;
      upsertMyApp(installed);
      setInstallToast({ title: card.title, instanceId: installed.instance_id });
      updateApp(card.selection.id, (app) => ({
        ...app,
        added: true,
        instance_id: installed.instance_id,
        app_url: installed.app_url,
      }));
      refresh();
      try { await discoverClient.confirmInstall(card.selection.id, installed.instance_id); }
      catch (cause) { setError(t("{{value0}} is installed, but its Install event could not be recorded: {{value1}}", { value0: card.title, value1: cause instanceof Error ? cause.message : "Please retry" })); }
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      if (currentRef.current.userId === userId) {
        const message = cause instanceof Error ? cause.message : t("The app could not be added.");
        setError(message);
        setInstallResult({ id: card.selection.id, status: "failed", message });
      }
    } finally {
      addLock.current = false;
      setAdding(false);
    }
  }
  useEffect(() => {
    if (!userId || !entry || entry.selection.kind !== "discover" || entry.added)
      return;
    if (consumePendingLibraryAdd(entry.selection.id)) void add(entry);
    // The pending action was recorded before the sign-in round trip.
  }, [userId, entry?.selection.id, entry?.added]);
  return (
    <RecordedPreviewProvider enabled={!enlarged}>
    {gpuPlacement.dialog}
    <div
      ref={panels}
      className={`discovery-workspace discovery-workspace--${view}${!favoriteTiles.length ? " discovery-workspace--empty-library" : ""}${enlarged ? " discovery-workspace--enlarged" : ""}${showLibraryRail ? "" : " discovery-workspace--no-rail"}`}
    >
      {switching || viewError || error ? (
        <div
          className="discovery-view-status"
          role={viewError || error ? "alert" : "status"}
        >
          {viewError ?? error ?? t("Switching…")}
        </div>
      ) : null}
      {feedback ? <div className="discovery-action-feedback" role="status">{feedback}</div> : null}
      {installToast ? (
        <div className="discovery-install-toast" role="status">
          <span className="discovery-install-toast-icon" aria-hidden="true"><CircleCheck size={20} /></span>
          <span className="discovery-install-toast-copy">
            <strong><Message message="Saved to Library" /></strong>
            <span><Message message="{{value0}} is ready in your Library." values={{ value0: installToast.title }} /></span>
          </span>
          <button type="button" className="discovery-install-toast-view" onClick={() => { const id = installToast.instanceId; setInstallToast(null); onNavigate(routeAfterAdd(id)); }}>
            <ArrowUpRight size={17} aria-hidden="true" /><Message message="View" />
          </button>
          <button type="button" className="discovery-install-toast-close" aria-label={t("Dismiss")} onClick={() => setInstallToast(null)}><X size={16} aria-hidden="true" /></button>
        </div>
      ) : null}
      {selected.detailError && !selected.failure?.notFound ? <div className="discovery-view-status" role="alert">
        <Message message={"App details could not load: "} />{selected.detailError}{" "}
        <button type="button" onClick={selected.refresh}><Message message={"Retry statistics"} /></button>
      </div> : null}
      {catalogue.status === "error" ? <div className="discovery-view-status" role="alert">
        <Message message={"Statistics and apps could not load: "} />{t(catalogue.error)}{" "}
        <button type="button" onClick={refresh}><Message message={"Retry statistics"} /></button>
      </div> : null}
      {sheet && (showLibraryRail || sheet === "discover") ? (
        <button
          className="discovery-sheet-shade"
          aria-label={t("Close panel")}
          onClick={closePanel}
        />
      ) : null}
      {/* Library rail — off unless explicitly enabled (see
          isDiscoveryLibraryRailEnabled). The markup stays so turning the flag
          back on restores the favorites shortcuts and Import app entry. */}
      {showLibraryRail ? (
        <aside
          className={`discovery-rail${sheet === "library" ? " discovery-panel--open" : ""}`}
          aria-label={t("Library")}
          role={sheet === "library" ? "dialog" : undefined}
          aria-modal={sheet === "library" ? true : undefined}
        >
          <button
            className="discovery-panel-close"
            aria-label={t("Close Library")}
            onClick={closePanel}
          >
            <X />
          </button>
          <div className="discovery-rail-section" role="group" aria-label={t("Explore apps")}>
            <p><Message message="Explore apps" /></p>
            <button type="button" aria-current="page" onClick={() => void returnToGrid()}><Message message="Discover" /></button>
          </div>
          <div className="discovery-rail-section" role="group" aria-label={t("Your space")}>
            <p><Message message="Your space" /></p>
            <button type="button" onClick={() => onNavigate("/?view=my-apps")}><Library size={18} aria-hidden="true" /><Message message="Library" /></button>
            <p className="discovery-library-hint"><Message message="Your saved apps and favorites." /></p>
          </div>
          <p className="discovery-favorites-label"><Message message={"Favorites"} /></p>
          <div className="discovery-launcher">
            {favoriteTiles.map((tile) => {
              const icon = (
                <>
                  <AppIcon
                    name={tile.name}
                    seed={tile.id}
                    size={32}
                    listingIconUrl={tile.icon}
                    {...("group" in tile
                      ? {
                          color: tile.group.icon_color,
                          glyph: tile.group.icon_glyph,
                          imageUrl: iconImageUrl(tile.group.icon_image_path),
                        }
                      : {})}
                  />
                  <span>{tile.name}{selectionKey(tile.selection) === key && launchFeedback.phase === "failed" ? <small className="app-launch-failed-label"><Message message="Launch failed" /></small> : null}</span>
                </>
              );
              return "href" in tile && typeof tile.href === "string" ? (
                <a key={tile.id} aria-label={t("Open {{value0}}", { value0: tile.name })} {...appLinkProps(tile.href)}>
                  {icon}
                </a>
              ) : (
                <button
                  key={tile.id}
                  onClick={() => {
                    const instanceId = "launchInstanceId" in tile ? tile.launchInstanceId
                      : tile.selection.kind === "instance" ? tile.selection.id : null;
                    if (instanceId) {
                      onNavigate(routeAfterAdd(instanceId));
                      return;
                    }
                    void (async () => {
                      if (view === "list" && !(await changeView("play"))) return;
                      select(tile.selection);
                    })();
                  }}
                  aria-pressed={selectionKey(tile.selection) === key}
                  aria-label={t("Open {{value0}}", { value0: tile.name })}
                >
                  {icon}
                </button>
              );
            })}
          </div>
          {!userId ? (
            <button className="discovery-text-button" onClick={requestSignIn}><Message message="Sign in to save apps" /></button>
          ) : library.error ? (
            <div role="alert">
              {t(library.error)}
              <button onClick={() => void refreshMyApps({ force: true })}><Message message={"Retry"} /></button>
            </div>
          ) : null}
          {userId && !favoriteTiles.length ? (
            <p className="discovery-favorites-empty"><Message message={"Choose “Add to favorites” in the app’s ⋮ menu to add a shortcut here."} /></p>
          ) : null}
          {favorites.error ? <p role="alert">{t(favorites.error)}</p> : null}
          <button
            className="discovery-import"
            onClick={() => (userId ? onNavigate("/apps/new") : requestSignIn())}
          ><Message message={"+ Import app"} /></button>
          <a className="discovery-help-link" href={docsRoute(locale, "using-apps")}><Message message="How to use ato.run" /></a>
        </aside>
      ) : null}
      <main
        ref={stage}
        className="discovery-stage"
        aria-hidden={view === "list" || undefined}
      >
        {/* The window shell is mounted for the Grid → Surface transition too:
            the launch Splash must not be a second, differently positioned copy
            outside the window. */}
        <AppWindowFrame
          appKey={key}
          title={title}
          trialRemainingSeconds={!publicTrialExpired ? publicClock.remainingSeconds ??
            (trial.state.kind === "ready" ? trial.remainingSeconds : null) : null}
          onReload={publicApp.instanceId && !publicTrialExpired ? retrySurface : undefined}
          onSurfaceReady={surfaceReady}
          onFrameRevealed={revealFrame}
          icon={
            selection && !selected.failure?.notFound ? (
              <AppIcon
                name={title}
                seed={instance?.compute_id ?? selection.id}
                size={18}
                color={instance?.icon_color}
                glyph={instance?.icon_glyph}
                imageUrl={iconImageUrl(instance?.icon_image_path ?? null)}
                listingIconUrl={
                  instance?.listing_icon_url ??
                  entry?.iconUrl ??
                  (selection.kind === "capsule" &&
                  capsuleIdentity?.target === selection.id
                    ? capsuleIdentity.iconUrl
                    : null)
                }
              />
            ) : undefined
          }
          enlarged={enlarged}
          canEnlarge={!publicTrialExpired && (launchFeedback.phase === "ready" || launchFeedback.phase === "revealed") && (Boolean(previewUrl) || trial.state.kind === "ready" || selection?.kind === "capsule" || Boolean(instance && externalUrl))}
          onEnlarge={() => (enlarged ? leaveFocus() : enterFocus())}
          // Leaving the Preview is explicit: a visible close action and Escape
          // no longer depend on the ⋮ menu or the browser Back button.
          onClose={() => void returnToGrid()}
          externalUrl={publicTrialExpired ? null : externalUrl}
          onOpenExternal={launchFeedback.openExternal}
          favorite={favorites.ids.includes(key)}
          onToggleFavorite={
            selection && !selected.failure?.notFound
              ? () => {
                  if (!userId) { requestSignIn(); return; }
                  const result = favorites.toggle(key);
                  if (result) setFeedback(result === "added" ? "Added a shortcut to Favorites" : "Removed the Favorites shortcut");
                }
              : undefined
          }
          trialInBar={wideScreen}
          launching={surfaceVisible && (launching || launchViewPending)}
          launchComplete={launchFinished}
          onCancelLaunch={launchInProgress ? cancelLaunch : undefined}
          headerActions={
            selection && !selected.failure?.notFound ? (
              <AppHeaderActions
                title={title}
                entry={summaryEntry}
                signedIn={Boolean(userId)}
                installing={adding}
                installResult={installResult && installResult.id === summaryEntry?.selection.id ? installResult.status : null}
                installError={installResult?.id === summaryEntry?.selection.id ? installResult?.message : null}
                liking={pendingLike.has(summaryEntry?.selection.id ?? "")}
                onInstall={() => void add(summaryEntry)}
                onLike={() => summaryEntry && void like(summaryEntry)}
              />
            ) : undefined
          }
          titleDetails={
            selection && !selected.failure?.notFound ? (
              <AppTitleDetails title={title} entry={summaryEntry} onDetails={() => openDetails(selection)} />
            ) : undefined
          }
          actions={
            <>
              {entry?.selection.kind === "discover" && (entry.previewUrl || entry.publicAccessAvailable || entry.publicTryAvailable) ? (
                <a href={`/settings/embeds?app=${encodeURIComponent(entry.selection.id)}`}>{t("Embed app")}</a>
              ) : null}
              {entry?.selection.kind === "discover" ? (
                <button type="button" onClick={(event) => {
                  event.currentTarget.closest("details")?.removeAttribute("open");
                  setReporting({ id: entry.selection.id, title: entry.title });
                }}>
                  <Flag size={16} aria-hidden="true" />
                  <Message message="Report" /></button>
              ) : null}
              {usage.error ? <div className="discovery-menu-note" role="alert"><Message message={"Discover use could not be recorded: "} />{t(usage.error)} <button type="button" onClick={usage.retry}><Message message={"Retry recording"} /></button></div> : null}
              {trial.state.kind === "ready" ? (
                <button aria-label={t("Reset Try")} onClick={resetTrial}>
                  <RotateCcw size={16} aria-hidden="true" />
                  <Message message={"Reset Try"} /></button>
              ) : null}
              {trial.state.kind === "ready" ||
              trial.state.kind === "stop_failed" ? (
                <button onClick={() => void trial.stop()}>
                  {trial.state.kind === "stop_failed"
                    ? t("Retry stop")
                    : trial.remainingSeconds === null ? t("Stop") : t("Stop · {{value0}}s", { value0: trial.remainingSeconds })}
                </button>
              ) : trial.state.kind === "stopping" ? (
                <span role="status"><Message message={"Stopping…"} /></span>
              ) : undefined}
            </>
          }
        >
          <AppLaunchFeedback phase={publicTrialExpired ? "idle" : launchFeedback.phase}
            offline={!online}
            errorMessage={trial.state.kind === "ready" ? "The app hasn't finished loading. Retry or open it in a new tab." : launchFailure} retryable={launchRetryable}
            externalUrl={trial.state.kind === "ready" ? externalUrl : null}
            retryAt={trial.failure?.retryAt ?? selected.failure?.retryAt}
            onReturnToTry={trial.failure?.activeAppId ? () => onNavigate(discoveryNavigationTarget({ ...urlState, selection: { kind: "discover", id: trial.failure!.activeAppId! } })) : undefined}
            onRetry={retryLaunch} onBack={() => void returnToGrid()}>
          {/* One Splash, mounted once, inside the window body. It stays for the
              Grid → Surface transition and every internal launch phase. */}
          {splashActive ? <AppSplash name={splashName} icon={splashIcon} /> : null}
          {launchViewPending ? null : !surfaceDiscarded && <>
          <Suspense fallback={null}><RetainedAppFrames
            selectionKey={key}
            cooperative={frameRetainedApp?.cooperative ?? true}
            onRetry={retrySurface}
            onBack={() => void returnToGrid()}
            backLabel="Back to grid"
            loadingOverlay={false}
            onSurfaceReady={url => surfaceReady(url, false)}
            app={frameRetainedApp} /></Suspense>
          {publicTrialExpired ? <PublicTrialExpired key={key} seconds={previewUrl ? publicClock.limitSeconds : trial.limitSeconds} onBack={() => void returnToGrid()} onAddToLibrary={entry?.selection.kind === "discover" ? () => void add(entry) : undefined} adding={adding}
            onRetry={retryPossible ? retryExpiredTry : undefined} retryAt={trial.failure?.retryAt ?? 0} /> : selection?.kind === "capsule" ? (
            <DeferredView key={`${userId}:${key}`} fallback={<AppSplash inline name={knownTitle} icon={splashIcon} />}>
              <CapsuleStage
                key={`${userId}:${key}`}
                target={selection.id}
                onMetadata={onCapsuleMetadata}
                accountState={accountState}
                onAccountRefresh={onAccountRefresh}
                onSignIn={requestSignIn}
                onNavigate={onNavigate}
              />
            </DeferredView>
          ) : selection?.kind === "instance" && accountState.status === "signed_in" ? (
            <InstanceLaunch
              key={`${userId}:${selection.id}`}
              instanceId={selection.id}
              name={knownTitle ?? undefined}
              hideLoading
              onResolved={onResolved}
              onFailure={() => setInstanceFailure(selection.id)}
              onBack={() => onNavigate("/?view=my-apps")}
            />
          ) : previewUrl && publicApp.instanceId && publicApp.expiresAt && entry?.publishedVersion ? null : previewUrl ? (
            null
          ) : entry?.publicAccessAvailable &&
            entry.executionClass === "Browser" ? (
            <div className="discovery-stage-empty">
              {publicApp.error ? (
                <>
                  <h2><Message message="Couldn't open this app" /></h2>
                  <p role="alert">{t(publicApp.error)}</p>
                  <button className="btn" onClick={publicApp.retry}><Message message={"Retry"} /></button>
                </>
              ) : null}
              <button className="btn" onClick={() => void returnToGrid()}><Message message="Back to grid" /></button>
            </div>
          ) : entry?.publicTryAvailable ? trial.state.kind === "ended" ? (
            // A Try the visitor stopped is a surface state, not a Run: the same
            // compact view as expiry, with only the actions that can work now.
            <SurfaceState
              className="discovery-trial-ended"
              icon="clock"
              title={<Message message="Try ended" />}
              description={<Message message="You stopped this Try. Start another one to keep using the app." />}
              actions={[
                { label: <Message message="Start another Try" />, kind: "primary", onClick: () => { setLaunchAttempt(value => value + 1); trial.start(); } },
                { label: <Message message="Browse apps" />, kind: "secondary", onClick: () => void returnToGrid() },
              ]}
            />
          ) : (
            <DeferredView key={`${userId}:${key}`} fallback={launching ? null : <AppSplash inline name={title} icon={splashIcon} />}>
              <PreviewPlayer
                onSurfaceReady={url => surfaceReady(url, false)}
                onOpenExternal={launchFeedback.openExternal}
                key={`${userId}:${key}`}
                mode="window"
                busyOverlay={false}
                surfaceLoadingOverlay={false}
                item={previewItem(entry)}
                state={trial.state}
                elapsedSeconds={trial.elapsedSeconds}
                onStart={() => { setLaunchAttempt(value => value + 1); const intent = usage.begin(entry); if (intent) history.replaceState({ ...history.state, atoDiscoveryIntent: intent }, ""); trial.start(); }}
                onStop={() => { trial.cancelStart(); void trial.stop(); }}
                onCancelStart={() => { trial.cancelStart(); void trial.stop(); }}
                onRetry={retryLaunch}
                onSignIn={requestSignIn}
                externalUrl={externalUrl}
                startLabel={
                  userId || entry.publicAccessAvailable
                    ? trialStartLabel(entry.publicTrialPolicy)
                    : "Sign in to Try"
                }
                stopFailureMessage="Couldn't confirm the Try has stopped. Retry stop to confirm its release."
              />
            </DeferredView>
          ) : splashActive ||
            launchFeedback.phase === "loading" ||
            launchFeedback.phase === "slow" ||
            launchFeedback.phase === "revealed" ? (
            // The launch Surface state already says "preparing"; rendering the
            // stage's own empty/unavailable panel underneath it produced two
            // contradictory messages at the same time.
            null
          ) : (
            <div className="discovery-stage-empty">
              {selected.error ? (
                <><h2><Message message="Couldn't open this app" /></h2><p role="alert">{t(selected.error)}</p><button type="button" className="btn" onClick={selected.retry}><Message message={"Retry app"} /></button><button type="button" className="btn" onClick={() => void returnToGrid()}><Message message="Back to grid" /></button></>
              ) : !selection && catalogue.status === "loading" ? (
                <p role="status"><Message message={"Loading apps…"} /></p>
              ) : resolved ? (
                <>
                  <AppIcon
                    name={title}
                    seed={key}
                    size={72}
                    listingIconUrl={entry?.iconUrl}
                  />
                  <p>{entry?.description}</p>
                  {entry?.selection.kind === "discover" && !entry.added ? (
                    <p><Message message={"This app is currently unavailable for public use."} /></p>
                  ) : null}
                  {entry?.externalUrl ? (
                    <a
                      className="btn"
                      href={entry.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Play size={16} aria-hidden="true" />
                      <Message message={"Open"} /></a>
                  ) : (
                    <button
                      className="btn"
                      onClick={() => void add()}
                      disabled={adding}
                      aria-label={
                        entry?.added ? t("Open my app") : t("Add to My Apps")
                      }
                    >
                      {entry?.added ? (
                        <Play size={16} aria-hidden="true" />
                      ) : (
                        <Plus size={16} aria-hidden="true" />
                      )}
                      {entry?.added ? t("Open") : t("Add")}
                    </button>
                  )}
                </>
              ) : (
                <p role="status">
                  {selection
                    ? t("This app isn't available.")
                    : t("No apps available.")}
                </p>
              )}
            </div>
          )}
          </>}
          </AppLaunchFeedback>
        </AppWindowFrame>
      </main>
      <aside
        id="discovery-discover-panel"
        ref={feed}
        onScroll={(event) => {
          const panel = event.currentTarget;
          scrollPositions.current[view] = panel.scrollTop;
        }}
        tabIndex={-1}
        className={`discovery-feed${sheet === "discover" ? " discovery-panel--open" : ""}`}
        aria-label={t("Discover")}
        role={sheet === "discover" ? "dialog" : undefined}
        aria-modal={sheet === "discover" ? true : undefined}
      >
        <button
          className="discovery-panel-close"
          aria-label={t("Close Discover")}
          onClick={closePanel}
        >
          <X />
        </button>
        {view === "list" && !search.query && !urlState.query && !urlState.themeId ? (
          heroHidden ? (
            <div className="discovery-hero-reveal">
              <button
                type="button"
                className="discovery-hero-toggle discovery-hero-toggle--show"
                aria-controls="discovery-intro"
                aria-expanded={false}
                onClick={toggleHero}
              >
                <CircleHelp size={16} aria-hidden="true" />
                <Message message="What’s this site?" />
              </button>
            </div>
          ) : (
            <section
              id="discovery-intro"
              className={`discovery-intro${wideScreen ? " discovery-intro--cards" : ""}`}
              aria-labelledby="discovery-intro-title"
            >
              <HeroVideoBackdrop />
              <button
                type="button"
                className="discovery-hero-toggle discovery-hero-toggle--hide"
                aria-label={t("Hide hero")}
                title={t("Hide hero")}
                aria-controls="discovery-intro"
                onClick={toggleHero}
              >
                <EyeOff size={16} aria-hidden="true" />
              </button>
              {heroCopy === "c" ? (
                <div className="discovery-intro-copy" data-hero-variant="c">
                  <div className="discovery-hero-brand">
                    <img src="/icons/ato-icon-no-border.png" alt="" width={40} height={40} />
                    <span><Message message="ato.run" /></span>
                  </div>
                  <HeroHeadline message="Discover. Try. [Run]." />
                  <p><Message message="Explore open-source repos and run them instantly in your browser." /></p>
                </div>
              ) : heroCopy === "b" ? (
                <div className="discovery-intro-copy" data-hero-variant="b">
                  <div className="discovery-hero-brand">
                    <img src="/icons/ato-icon-no-border.png" alt="" width={40} height={40} />
                    <span><Message message="ato.run" /></span>
                  </div>
                  <HeroHeadline message="More than [apps]." />
                  <p><Message message="Apps, games, tools, media, and AI — open them here. No install, no setup." /></p>
                </div>
              ) : (
                <div className="discovery-intro-copy" data-hero-variant="a">
                  <div className="discovery-hero-brand">
                    <img src="/icons/ato-icon-no-border.png" alt="" width={40} height={40} />
                    <span><Message message="ato.run" /></span>
                  </div>
                  <HeroHeadline message="Discover what’s [open]." />
                  <p><Message message="Try it and find your favorite. All open source, no install, no setup." /></p>
                </div>
              )}
              <div className="discovery-intro-actions">
                <button type="button" className="ato-button ato-button--primary" onClick={() => {
                  // Search lives in the header; Explore goes to the list
                  // and, on wide screens, puts the cursor in the search field.
                  feed.current?.querySelector(".discovery-filters")?.scrollIntoView?.({ block: "start" });
                  if (wideScreen) document.getElementById("header-app-search")?.focus({ preventScroll: true });
                }}><Message message="Explore" /><ArrowDown size={18} aria-hidden="true" /></button>
                <a className="ato-button ato-button--secondary" href={docsRoute(locale, "what-is-ato")}><Message message="Learn about Ato" /></a>
              </div>
              {wideScreen && editorial.cards?.length !== 0 ? (
                <HeroAppCards cards={editorial.cards} onOpen={card => void openCard(card)} />
              ) : editorial.demos.length ? (
                <HeroDemoPanel demos={editorial.demos} onOpen={card => void openCard(card)} />
              ) : <div className="discovery-hero-demo discovery-hero-demo--pending" aria-hidden="true"><span /></div>}
              <ol className="discovery-intro-steps">
                <li><Search size={16} aria-hidden="true" /><Message message="Find" /></li>
                <li><Play size={16} aria-hidden="true" /><Message message="Open" /></li>
                <li><AppWindow size={16} aria-hidden="true" /><Message message="Use" /></li>
              </ol>
            </section>
          )
        ) : null}
        <>
          <DiscoveryFilters categories={themes} category={urlState.themeId} sort={sort}
            view={view}
            allowRatings={qaVisible()} onSort={setSort}
            search={(
            <div className="discovery-sheet-search ato-search">
              <Search size={20} aria-hidden="true" />
              <input
                id="discovery-search"
                type="search"
                aria-label={t("Search apps")}
                placeholder={t("Search apps…")}
                maxLength={200}
                value={search.query}
                onChange={(event) => search.setQuery(event.target.value)}
              />
            </div>
            )}
            // From the route, never the retained Preview app: choosing a
            // category on the Grid must not reopen the last Preview.
            onCategory={themeId => onNavigate(discoveryNavigationTarget({ ...routeState, themeId }))} />
          {view === "list" ? (
            <DiscoveryAppliedFilters query={routeState.query} sort={sort}
              // Clearing the query updates the URL through SearchProvider.
              onClearQuery={() => search.setQuery("")}
              onClearSort={() => setSort("recommended")} />
          ) : null}
          {view === "list" && catalogue.status !== "error" ? (
            // A filter or search changes the list silently otherwise; the
            // count is the feedback that the Grid actually narrowed.
            <p className="discovery-result-count" role="status" aria-live="polite">
              {catalogue.status === "loading" && !cards.length
                ? t("Loading apps…")
                : plural(displayCards.length, "{{value0}} app", "{{value0}} apps", {
                    value0: displayCards.length,
                  })}
            </p>
          ) : null}
          {feedbackNotice ? (
            <p className="discovery-feedback-line" role="status">
              {feedbackNotice.action === "hidden"
                ? <Message message="Hidden from your feed" />
                : feedbackNotice.action === "helpful"
                  ? <Message message="Marked as helpful" />
                  : <Message message="Marked as not interested" />}
              <button type="button" className="settings-link" onClick={undoFeedback}><Message message="Undo" /></button>
            </p>
          ) : null}
          {catalogue.status === "error" && !cards.length ? (
            <div role="alert">
              {t(catalogue.error)}
              <button onClick={refresh}><Message message={"Retry"} /></button>
            </div>
          ) : catalogue.status === "loading" && !cards.length ? (
            <div className="discovery-card-grid discovery-grid-skeleton" role="status" aria-label={t("Loading apps…")}>
              {Array.from({ length: 6 }, (_, index) => <div key={index} className="discovery-skeleton-card" aria-hidden="true"><span /><span /><span /></div>)}
            </div>
          ) : displayCards.length === 0 ? (
            <div role="status"><p><Message message={"Nothing matches this search or category."} /></p>
              {search.query || urlState.themeId ? <button type="button" className="btn" onClick={() => { search.setQuery(""); onNavigate(discoveryNavigationTarget({ ...routeState, query: "", themeId: null }), { replace: true }); }}><Message message="Clear filters" /></button> : null}
            </div>

          ) : (
            <div ref={setCardGrid} className="discovery-card-grid">
              {exploreRows(displayCards.filter((card) => view === "list" || selectionKey(card.selection) !== key), {
                columns: gridColumns,
                // No ad delivery exists yet, so there is no sponsored stream.
                streams: [{ type: "featured", ids: features.map((feature) => feature.listingId), policy: featuredPolicyFor(gridColumns) }],
              }).map((row, rowIndex, rows) => {
                if (row.type === "featured") {
                  const feature = features.find((item) => item.listingId === row.featureId);
                  // Featured rows alternate sides: copy left, then copy right.
                  const featuredIndex = rows.slice(0, rowIndex).filter((previous) => previous.type === "featured").length;
                  return feature ? <FeaturedRow key={`featured:${row.featureId}`} feature={feature} mirrored={featuredIndex % 2 === 1} onOpen={card => void openCard(card)} onNavigate={onNavigate} /> : null;
                }
                if (row.type === "sponsored") return null;
                const offset = rows.slice(0, rowIndex).reduce((sum, previous) => sum + (previous.type === "organic" ? previous.apps.length : 0), 0);
                return row.apps.map((card, index) => (
                  <DiscoverFeedCard
                    key={selectionKey(card.selection)}
                    entry={card}
                    iconSize={28}
                    position={offset + index + 1}
                    view={measurementView}
                    onFeedback={action => dismissCard(card, action)}
                    selected={mayOpenSurface && selectionKey(card.selection) === key}
                    launchFailed={selectionKey(card.selection) === key && launchFeedback.phase === "failed"}
                    onSelect={() => openCard(card)}
                    onNavigate={onNavigate}
                  />
                ));
              })}
            </div>
          )}
          {catalogue.hasMore ? (
            <div className="discovery-more">
              <button ref={moreButton} type="button" className="ato-button ato-button--secondary" disabled={catalogue.loadingMore || catalogue.status === "loading"} onClick={catalogue.loadMore}>
                {catalogue.loadingMore ? t("Loading apps…") : t("Load more apps")}
              </button>
            </div>
          ) : null}
          {catalogue.status === "ready" && catalogue.error ? <p role="alert">{t(catalogue.error)}</p> : null}
        </>
        {view === "list" ? <GlobalFooter /> : null}
      </aside>
    </div>
    {reporting ? (
      <Suspense fallback={null}><ReportCapsuleModal
        key={reporting.id}
        signedIn={signedIn}
        submit={(body) => discoverClient.report(reporting.id, body)}
        onSignIn={() => { setReporting(null); requestSignIn(); }}
        onClose={() => setReporting(null)}
      /></Suspense>
    ) : null}
    {signInPrompt ? (
      <ConfirmDialog
        title={t("Sign in to save this app?")}
        confirmLabel="Continue to sign in"
        cancelLabel="Keep using this Try"
        tone="primary"
        onCancel={() => {
          clearPendingLibraryAdd();
          setSignInPrompt(false);
        }}
        onConfirm={() => {
          setSignInPrompt(false);
          onSignIn();
        }}
      >
        <p><Message message={"Signing in reloads ato.run and ends this Try. Its in-app changes, such as a game score or unsaved note, will not be saved. Add to Library saves a separate copy for later."} /></p>
      </ConfirmDialog>
    ) : null}
    </RecordedPreviewProvider>
  );
}
